import { useApolloClient } from '@apollo/client';
import React, { FC, PropsWithChildren, useEffect, useState } from 'react';

import { InvariantError } from '@apollo/client/utilities/globals';
import { usePatient } from 'hooks/usePatient/usePatient';
import { BugTracker } from 'kb-shared/utilities/bugTracker';

import { GET_FEATURE_FLAGS } from './FeatureFlags.graphql';
import { FeatureFlag, FeatureFlags } from './FeatureFlags.types';
import { extractFeatureFlags } from './FeatureFlags.utils';
import FeatureFlagsContext from './FeatureFlagsContext';

// Logout calls client.stop() and client.clearStore(), which reject in-flight queries with an
// InvariantError. Retry those instead of reporting them so global flags still load after logout.
const MAX_STORE_RESET_RETRIES = 3;

export const FeatureFlagsProvider: FC<PropsWithChildren> = ({ children }) => {
  const client = useApolloClient();
  const { isLoggedIn } = usePatient();
  const [featureFlags, setFeatureFlags] = useState<Partial<FeatureFlags>>({});
  const [loading, setLoading] = useState(true);
  // Which session the current flags belong to, so _loading is true in the same render
  // the session changes instead of one effect later.
  const [loadedFor, setLoadedFor] = useState<boolean | null>(null);
  const anonymous = isLoggedIn !== true;

  useEffect(() => {
    let cancelled = false;
    // Patient flags can include per-patient enables, so drop them on logout. Global flags are
    // a subset of patient flags and stay visible while the patient's flags load.
    if (anonymous) setFeatureFlags({});
    setLoading(true);

    const loadFlags = async (retriesLeft: number): Promise<void> => {
      try {
        const result = await client.query<{ featureFlags?: FeatureFlag[] }>({
          query: GET_FEATURE_FLAGS,
          fetchPolicy: 'no-cache',
          context: { anonymous }
        });
        if (cancelled) return;
        setFeatureFlags(extractFeatureFlags(result.data?.featureFlags));
      } catch (error) {
        if (cancelled) return;
        if (error instanceof InvariantError && retriesLeft > 0) return loadFlags(retriesLeft - 1);
        BugTracker.notify(error, 'FeatureFlagsQuery');
      }
      setLoadedFor(anonymous);
      setLoading(false);
    };

    loadFlags(MAX_STORE_RESET_RETRIES);

    return () => {
      cancelled = true;
    };
  }, [client, anonymous]);

  return (
    <FeatureFlagsContext.Provider
      value={{ ...featureFlags, _loading: loading || loadedFor !== anonymous }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};
